import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  BarChart3,
  TrendingUp,
  Calendar,
  CreditCard,
  Banknote,
  FileText,
  Package,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export default function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/sales?period=${period}`);
      setReport(response.data);
    } catch (error) {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-SA", { style: "currency", currency: "SAR" }).format(amount || 0);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });

  const COLORS = ["hsl(144, 69%, 19%)", "hsl(160, 84%, 39%)"];

  const cashTotal = report?.payment_summary?.cash?.total || 0;
  const cardTotal = report?.payment_summary?.card?.total || 0;
  const paymentData = [
    { name: "نقدي", value: cashTotal },
    { name: "شبكة", value: cardTotal },
  ];

  const dailyData = report?.daily_sales?.slice(-7) || [];

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[hsl(var(--primary))] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <header className="page-header">
        <h1 className="page-title">التقارير</h1>
      </header>

      <div className="page-content">
        {/* Period Filter */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "'Tajawal', sans-serif" }}>
            <Calendar size={20} />
            الفترة الزمنية
          </h3>
          <div className="flex gap-3">
            {[
              { key: "week", label: "أسبوع" },
              { key: "month", label: "شهر" },
              { key: "year", label: "سنة" },
            ].map((p) => (
              <Button
                key={p.key}
                data-testid={`period-${p.key}`}
                variant={period === p.key ? "default" : "outline"}
                onClick={() => setPeriod(p.key)}
                className={period === p.key ? "btn btn-primary" : ""}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp size={24} className="text-[hsl(var(--primary))]" />
            </div>
            <div className="text-2xl font-bold text-[hsl(var(--primary))]" data-testid="report-total-sales">
              {formatCurrency(report?.total_sales)}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">إجمالي المبيعات</p>
          </div>

          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <div className="flex items-center justify-between mb-4">
              <FileText size={24} className="text-blue-600" />
            </div>
            <div className="text-2xl font-bold" data-testid="report-invoice-count">
              {report?.total_invoices || 0}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">عدد الفواتير</p>
          </div>

          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 size={24} className="text-orange-600" />
            </div>
            <div className="text-2xl font-bold" data-testid="report-total-vat">
              {formatCurrency(report?.total_vat)}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">إجمالي الضريبة</p>
          </div>

          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp size={24} className="text-purple-600" />
            </div>
            <div className="text-2xl font-bold" data-testid="report-average">
              {formatCurrency(
                report?.total_invoices > 0 ? report.total_sales / report.total_invoices : 0
              )}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">متوسط الفاتورة</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <h3 className="font-semibold mb-6" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              المبيعات اليومية
            </h3>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => formatDate(v)} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [formatCurrency(value), "المبيعات"]} labelFormatter={(l) => formatDate(l)} />
                  <Bar dataKey="total" fill="hsl(144, 69%, 19%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[hsl(var(--muted-foreground))]">لا توجد بيانات</div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] p-6">
            <h3 className="font-semibold mb-6" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              توزيع طرق الدفع
            </h3>
            {paymentData.some((d) => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={paymentData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {paymentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[hsl(var(--muted-foreground))]">لا توجد بيانات</div>
            )}
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <Banknote size={18} className="text-[hsl(var(--primary))]" />
                <span className="text-sm">نقدي: {formatCurrency(cashTotal)}</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard size={18} className="text-green-600" />
                <span className="text-sm">شبكة: {formatCurrency(cardTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Products */}
        {report?.top_products?.length > 0 && (
          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            <div className="p-6 border-b border-[hsl(var(--border))]">
              <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                <Package size={20} />
                أعلى المنتجات مبيعاً
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[hsl(var(--muted)/0.3)]">
                    <th className="text-right p-4 font-medium text-[hsl(var(--muted-foreground))] text-sm">م</th>
                    <th className="text-right p-4 font-medium text-[hsl(var(--muted-foreground))] text-sm">المنتج</th>
                    <th className="text-right p-4 font-medium text-[hsl(var(--muted-foreground))] text-sm">الكمية</th>
                    <th className="text-right p-4 font-medium text-[hsl(var(--muted-foreground))] text-sm">الإجمالي</th>
                  </tr>
                </thead>
                <tbody>
                  {report.top_products.map((product, index) => (
                    <tr key={index} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.3)]">
                      <td className="p-4 text-sm text-[hsl(var(--muted-foreground))]">{index + 1}</td>
                      <td className="p-4 font-medium">{product.name}</td>
                      <td className="p-4">{product.quantity}</td>
                      <td className="p-4 font-semibold">{formatCurrency(product.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
